import React, { useState, useEffect } from "react";
import { Save, FileText, MessageSquare, FolderOpen } from "lucide-react";
import type { Task, TaskItemType, TaskFrequency } from "../../../types/task";
import { ApiService } from "../../../services/api";
import Modal from "../../../components/ui/Modal";
import DepartmentSelector from "../../../components/selectors/DepartmentSelector";

interface EditTaskModalProps {
  isOpen: boolean;
  task: Task | null;
  onClose: () => void;
  onSubmit: (taskId: string, updates: Partial<Task>) => void;
}

interface DepartmentDetail {
  id: string;
  name: string;
  color?: string;
}

export const EditTaskModal: React.FC<EditTaskModalProps> = ({ isOpen, task, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    itemType: "TEXT" as TaskItemType,
    frequency: "WEEKLY" as TaskFrequency,
    startDate: "",
    endDate: "",
    departmentIds: [] as string[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentSelectedDepartment, setCurrentSelectedDepartment] = useState<string>("");
  const [selectedDepartmentDetails, setSelectedDepartmentDetails] = useState<Record<string, DepartmentDetail>>({});

  useEffect(() => {
    if (isOpen && task) {
      setFormData({
        title: task.title,
        description: task.description,
        itemType: task.itemType,
        frequency: task.frequency,
        startDate: task.startDate.slice(0, 16),
        endDate: task.endDate.slice(0, 16),
        departmentIds: task.departments.map((d) => d.departmentId),
      });
      setErrors({});
      setCurrentSelectedDepartment("");

      const initialDepartmentDetails: Record<string, DepartmentDetail> = {};
      task.departments.forEach((dept) => {
        initialDepartmentDetails[dept.departmentId] = {
          id: dept.departmentId,
          name: dept.departmentName,
          color: dept.color,
        };
      });
      setSelectedDepartmentDetails(initialDepartmentDetails);
    }
  }, [isOpen, task]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleItemTypeChange = (type: TaskItemType) => {
    setFormData((prev) => ({ ...prev, itemType: type }));
  };

  const handleFrequencyChange = (frequency: TaskFrequency) => {
    setFormData((prev) => ({ ...prev, frequency }));
  };

  const handleDepartmentAdd = (departmentId: string, department?: any) => {
    if (departmentId && !formData.departmentIds.includes(departmentId)) {
      setFormData((prev) => ({
        ...prev,
        departmentIds: [...prev.departmentIds, departmentId],
      }));

      if (department) {
        setSelectedDepartmentDetails((prev) => ({
          ...prev,
          [departmentId]: {
            id: department.id,
            name: department.name,
            color: department.color,
          },
        }));
      }

      setCurrentSelectedDepartment("");

      if (errors.departmentIds) {
        setErrors((prev) => ({ ...prev, departmentIds: "" }));
      }
    }
  };

  const handleDepartmentRemove = (departmentId: string) => {
    setFormData((prev) => ({
      ...prev,
      departmentIds: prev.departmentIds.filter((id) => id !== departmentId),
    }));

    setSelectedDepartmentDetails((prev) => {
      const updated = { ...prev };
      delete updated[departmentId];
      return updated;
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Görev adı gereklidir";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Görev açıklaması gereklidir";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Başlangıç tarihi gereklidir";
    }

    if (!formData.endDate) {
      newErrors.endDate = "Bitiş tarihi gereklidir";
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) {
        newErrors.endDate = "Bitiş tarihi başlangıç tarihinden sonra olmalıdır";
      }
    }

    if (formData.departmentIds.length === 0) {
      newErrors.departmentIds = "En az bir departman seçmelisiniz";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!task) return;

    if (validate()) {
      onSubmit(task.id, {
        title: formData.title,
        description: formData.description,
        itemType: formData.itemType,
        frequency: formData.frequency,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        departments: formData.departmentIds.map((id, idx) => ({
          id: `td-${Date.now()}-${idx}`,
          departmentId: id,
          departmentName: selectedDepartmentDetails[id]?.name || "Departman",
          color: selectedDepartmentDetails[id]?.color,
        })),
      });
      onClose();
    }
  };

  if (!task) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Görev Düzenle" size="lg">
      <form onSubmit={handleSubmit}>
        <div className="px-6 pt-4 pb-6 space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Görev Adı <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.title ? "border-red-500" : "border-gray-300"
                }`}
              placeholder="Örn: Haftalık Ödev Gönderimi"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Görev Açıklaması <span className="text-red-500">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none ${errors.description ? "border-red-500" : "border-gray-300"
                }`}
              placeholder="Görev hakkında detaylı açıklama yazın..."
            />
            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Görev Sıklığı <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { value: "ONCE", label: "Bir Kez" },
                { value: "DAILY", label: "Günlük" },
                { value: "WEEKLY", label: "Haftalık" },
                { value: "MONTHLY", label: "Aylık" },
              ].map((freq) => (
                <button
                  key={freq.value}
                  type="button"
                  onClick={() => handleFrequencyChange(freq.value as TaskFrequency)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${formData.frequency === freq.value ? "bg-purple-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}>
                  {freq.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              İstenilen Öge <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => handleItemTypeChange("TEXT")}
                className={`px-4 py-2.5 border-2 rounded-lg transition-all ${formData.itemType === "TEXT" ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-gray-300"
                  }`}>
                <div className="flex items-center justify-center gap-2">
                  <MessageSquare className={`w-5 h-5 ${formData.itemType === "TEXT" ? "text-purple-600" : "text-gray-400"}`} />
                  <span className={`text-sm font-medium ${formData.itemType === "TEXT" ? "text-purple-900" : "text-gray-700"}`}>Yazı</span>
                </div>
              </button>

              <button
                type="button"
                disabled
                className="px-4 py-2.5 border-2 rounded-lg border-gray-200 bg-gray-100 cursor-not-allowed opacity-60">
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Dosya</span>
                </div>
              </button>

              <button
                type="button"
                disabled
                className="px-4 py-2.5 border-2 rounded-lg border-gray-200 bg-gray-100 cursor-not-allowed opacity-60">
                <div className="flex items-center justify-center gap-2">
                  <FolderOpen className="w-5 h-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Her İkisi</span>
                </div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Başlangıç Tarihi <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                id="startDate"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.startDate ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {errors.startDate && <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>}
            </div>

            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                Bitiş Tarihi <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                id="endDate"
                name="endDate"
                value={formData.endDate}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.endDate ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Departmanlar <span className="text-red-500">*</span>
            </label>

            {formData.departmentIds.length > 0 && (
              <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-sm font-medium text-purple-900 mb-3">Seçili Departmanlar ({formData.departmentIds.length})</div>
                <div className="flex flex-wrap gap-2">
                  {formData.departmentIds.map((deptId) => {
                    const dept = selectedDepartmentDetails[deptId];
                    return dept ? (
                      <span
                        key={deptId}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        {dept.color && <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: dept.color }} />}
                        <span className="truncate">{dept.name}</span>
                        <button
                          type="button"
                          onClick={() => handleDepartmentRemove(deptId)}
                          className="text-purple-600 hover:text-purple-800 font-bold transition-colors ml-1">
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            <DepartmentSelector
              value={currentSelectedDepartment}
              onChange={handleDepartmentAdd}
              placeholder="Departman seçin..."
              excludeIds={formData.departmentIds}
              allowClear={false}
              required={false}
              showColors={true}
              inline={true}
              className="text-sm"
            />

            {errors.departmentIds && <p className="mt-2 text-sm text-red-600">{errors.departmentIds}</p>}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all">
            İptal
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-5 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 hover:shadow-lg transition-all inline-flex items-center gap-2">
            <Save className="w-4 h-4" />
            Güncelle
          </button>
        </div>
      </form>
    </Modal>
  );
};